import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trash2, Loader2, Truck, Store, CreditCard, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { formatCLP, imgSrc, getCart, setCart, cartSubtotal, REGIONES } from "@/lib/tienda";

const API = process.env.REACT_APP_BACKEND_URL || "";

const F = ({ label, req, children }) => (
  <div><Label className="text-xs">{label}{req && <span className="text-red-500"> *</span>}</Label>
    <div className="mt-1">{children}</div></div>
);

export default function TiendaCheckout() {
  const navigate = useNavigate();
  const [cart, setCartState] = useState(getCart());
  const [c, setC] = useState({ nombre: "", email: "", telefono: "", direccion: "", comuna: "", region: "" });
  const [retiro, setRetiro] = useState(false);
  const [factura, setFactura] = useState({ requiere: false, rut: "", razon_social: "", giro: "" });
  const [envio, setEnvio] = useState(null); // {costo, gratis_desde}
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const subtotal = cartSubtotal();

  // estimación de envío en vivo según región
  useEffect(() => {
    if (retiro) { setEnvio({ costo: 0 }); return; }
    if (!c.region) { setEnvio(null); return; }
    axios.post(`${API}/api/tienda/envio-estimado`, { region: c.region, comuna: c.comuna, subtotal })
      .then((r) => setEnvio(r.data)).catch(() => setEnvio(null));
  }, [c.region, c.comuna, retiro, subtotal]);

  const setQty = (i, d) => {
    const next = [...cart];
    next[i].cantidad = Math.max(1, Math.min(99, next[i].cantidad + d));
    setCart(next); setCartState(next);
  };
  const removeItem = (i) => {
    const next = cart.filter((_, j) => j !== i);
    setCart(next); setCartState(next);
  };

  const pagar = async () => {
    if (!c.nombre || !c.email) return toast.error("Completa tu nombre y correo");
    if (!retiro && (!c.direccion || !c.region)) return toast.error("Completa la dirección y región de despacho");
    if (factura.requiere && (!factura.rut || !factura.razon_social)) return toast.error("Para factura necesitamos RUT y razón social");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/tienda/checkout`, {
        items: cart.map((x) => ({ producto_id: x.producto_id, cantidad: x.cantidad })),
        comprador: c, retiro_tienda: retiro, factura,
      });
      setCart([]);
      if (r.data.init_point) {
        window.location.assign(r.data.init_point); // → Mercado Pago
      } else {
        toast.success(`Pedido ${r.data.folio} recibido — te contactaremos para coordinar el pago`);
        navigate(`/productos/gracias?folio=${r.data.folio}&r=pendiente`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos procesar la compra");
    } finally { setBusy(false); }
  };

  const total = subtotal + (envio?.costo || 0);

  if (cart.length === 0) {
    return (
      <div className="bg-[#F8FAFC] min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Tu carrito está vacío</p>
          <Link to="/productos"><Button className="bg-[#384091] text-white">Ir a la tienda</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-[70vh]">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 max-w-5xl">
        <Link to="/productos" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#384091] mb-5">
          <ArrowLeft className="h-4 w-4" /> Seguir comprando
        </Link>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#111827] mb-6">Finalizar compra</h1>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-8">
          {/* datos */}
          <div className="space-y-6">
            <div className="rounded-2xl bg-white border border-gray-100 p-5 space-y-3">
              <h2 className="font-semibold text-gray-800">Tus datos</h2>
              <F label="Nombre y apellido" req><Input value={c.nombre} onChange={(e) => u("nombre", e.target.value)} className="h-11 bg-white" /></F>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Correo (recibirás el comprobante)" req>
                  <Input type="email" value={c.email} onChange={(e) => u("email", e.target.value)} className="h-11 bg-white no-uppercase" /></F>
                <F label="Teléfono"><Input type="tel" value={c.telefono} onChange={(e) => u("telefono", e.target.value)} className="h-11 bg-white no-uppercase" placeholder="+56 9…" /></F>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 p-5 space-y-3">
              <h2 className="font-semibold text-gray-800">Entrega</h2>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setRetiro(false)}
                  className={`rounded-xl border-2 p-3 text-sm font-semibold flex items-center gap-2 justify-center ${!retiro ? "border-[#0FB4B7] bg-[#0FB4B7]/5 text-[#0D999C]" : "border-gray-200 text-gray-500"}`}>
                  <Truck className="h-4 w-4" /> Despacho
                </button>
                <button onClick={() => setRetiro(true)}
                  className={`rounded-xl border-2 p-3 text-sm font-semibold flex items-center gap-2 justify-center ${retiro ? "border-[#0FB4B7] bg-[#0FB4B7]/5 text-[#0D999C]" : "border-gray-200 text-gray-500"}`}>
                  <Store className="h-4 w-4" /> Retiro en tienda
                </button>
              </div>
              {!retiro && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="sm:col-span-2"><F label="Dirección" req><Input value={c.direccion} onChange={(e) => u("direccion", e.target.value)} className="h-11 bg-white" placeholder="Calle, número, depto/oficina" /></F></div>
                  <F label="Comuna"><Input value={c.comuna} onChange={(e) => u("comuna", e.target.value)} className="h-11 bg-white" /></F>
                  <F label="Región" req>
                    <Select value={c.region} onValueChange={(v) => u("region", v)}>
                      <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {REGIONES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </F>
                </div>
              )}
              {retiro && <p className="text-xs text-gray-500">Retira en Los Militares 5620, Las Condes — te avisaremos cuando esté listo.</p>}
            </div>

            <div className="rounded-2xl bg-white border border-gray-100 p-5 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={factura.requiere} onCheckedChange={(v) => setFactura((p) => ({ ...p, requiere: !!v }))} />
                <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-[#384091]" /> Necesito factura
                </span>
              </label>
              {factura.requiere && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <F label="RUT empresa" req><Input value={factura.rut} onChange={(e) => setFactura((p) => ({ ...p, rut: e.target.value }))} className="h-11 bg-white no-uppercase" /></F>
                  <F label="Razón social" req><Input value={factura.razon_social} onChange={(e) => setFactura((p) => ({ ...p, razon_social: e.target.value }))} className="h-11 bg-white" /></F>
                  <F label="Giro"><Input value={factura.giro} onChange={(e) => setFactura((p) => ({ ...p, giro: e.target.value }))} className="h-11 bg-white" /></F>
                </div>
              )}
            </div>
          </div>

          {/* resumen */}
          <div className="rounded-2xl bg-white border border-gray-100 p-5 h-fit lg:sticky lg:top-24">
            <h2 className="font-semibold text-gray-800 mb-3">Tu pedido</h2>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {cart.map((x, i) => (
                <div key={x.producto_id} className="flex items-center gap-3">
                  <div className="h-14 w-14 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
                    {x.imagen && <img src={imgSrc(x.imagen)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2">{x.nombre}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <button className="h-6 w-6 rounded border text-sm" onClick={() => setQty(i, -1)}>−</button>
                      <span className="text-xs w-5 text-center">{x.cantidad}</span>
                      <button className="h-6 w-6 rounded border text-sm" onClick={() => setQty(i, 1)}>+</button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#384091]">{formatCLP(x.precio * x.cantidad)}</p>
                    <button className="text-red-400 hover:text-red-600" onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatCLP(subtotal)}</span></div>
              <div className="flex justify-between text-gray-600">
                <span>Despacho estimado</span>
                <span>{retiro ? "Gratis (retiro)" : envio == null ? "según región" : envio.costo === 0 ? "¡Gratis!" : formatCLP(envio.costo)}</span>
              </div>
              {!retiro && envio?.gratis_desde > 0 && subtotal < envio.gratis_desde && (
                <p className="text-[11px] text-[#0D999C]">Envío gratis desde {formatCLP(envio.gratis_desde)}</p>
              )}
              <div className="flex justify-between font-bold text-[#111827] text-base pt-1">
                <span>Total</span><span>{formatCLP(total)}</span>
              </div>
            </div>
            <Button className="w-full h-13 py-3.5 mt-4 bg-[#009EE3] hover:bg-[#0087c3] text-white font-semibold"
              onClick={pagar} disabled={busy}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-5 w-5 mr-2" /> Pagar con Mercado Pago</>}
            </Button>
            <p className="text-[11px] text-gray-400 text-center mt-2">
              Pago seguro procesado por Mercado Pago. Recibirás tu comprobante por correo.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
